// 고객사 기준 정보 — 왼쪽 «고객사별 운영 기준» 카드(검색·센터·운영·영업·상태·빠른 필터 + 목록 표 + 본사 편집이면 등록 모달).
// 필터는 화면이 거른다(utils: filterCustomers) — 이 카드는 거른 결과(items)만 받아 그린다.
import { useEffect, useRef, useState } from 'react';
import { workCustomersAPI } from '../../api/client';
import { toast } from '../../components/AppDialog';
import { Button, SearchInput } from '../../components/ui';
import { COLORS, badge, card, input } from '../../utils/uiStyles';
import { KIND_OPTIONS, QUICK_FILTERS, STATUS_ENDED, STATUS_OPTIONS, dash, errorText } from './workCustomerLabels';

export const LIST_COLUMNS = ['NO', '업체명', '센터', '영업', '운영 담당자(정)', '센터담당'];

const th = { textAlign: 'left', padding: '7px 8px', fontSize: '12px', fontWeight: 500, color: COLORS.textSub, borderBottom: `1px solid ${COLORS.border}`, whiteSpace: 'nowrap' };
const td = { padding: '7px 8px', fontSize: '13px', color: COLORS.text, borderBottom: `1px solid ${COLORS.borderSoft}`, verticalAlign: 'top' };
const select = { ...input, width: 'auto', minWidth: '110px', padding: '5px 8px', fontSize: '12px' };
const nameBtn = { background: 'transparent', border: 0, padding: 0, font: 'inherit', color: 'inherit', textAlign: 'left', cursor: 'pointer', fontWeight: 500 };
const toggleBtn = (on) => ({
  ...badge(on ? 'blue' : 'gray'), border: `1px solid ${on ? COLORS.primaryBorder : COLORS.border}`, cursor: 'pointer',
});

// 등록 모달 칸 — 키는 서버 BASIC_FIELDS(이름만 필수). 사유는 이력에 남는다.
const CREATE_FIELDS = [
  { key: 'name', label: '업체명 *', required: true },
  { key: 'no', label: 'NO', inputMode: 'numeric' },
  { key: 'kind', label: '기존/신규', kind: 'select' },
  { key: 'open_month', label: '오픈 연월', placeholder: '2025-03' },
  { key: 'centers_raw', label: '센터', placeholder: '오산/오산3' },
  { key: 'sales', label: '영업' },
  { key: 'sales_team', label: '영업팀' },
  { key: 'ops_main', label: '운영 담당자(정)' },
  { key: 'ops_sub', label: '운영 담당자(부)' },
  { key: 'center_staff', label: '센터담당' },
  { key: 'douzone_code', label: '더존 거래처코드' },
  { key: 'contact_channel', label: '고객사 연락 채널' },
  { key: 'reason', label: '사유', placeholder: '등록 사유(선택)' },
];

function CreateCustomerModal({ onClose, onCreated }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const firstRef = useRef(null);
  // 닫기 함수는 부모가 매번 새로 만든다 — ref 로 들고 있어야 열 때 한 번만 첫 칸에 포커스를 준다.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') closeRef.current(); };
    document.addEventListener('keydown', onKey);
    if (firstRef.current) firstRef.current.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const name = String(form.name || '').trim();

  const submit = async (e) => {
    e.preventDefault();
    if (!name || busy) return;
    const body = {};
    CREATE_FIELDS.forEach(({ key }) => {
      const v = String(form[key] || '').trim();
      if (v) body[key] = v;
    });
    setBusy(true);
    setError('');
    try {
      const res = await workCustomersAPI.create(body);
      toast('등록했습니다');
      onCreated(res && res.data ? res.data.id : null);
    } catch (err) {
      setError(errorText(err, '고객사를 등록하지 못했습니다'));
      setBusy(false);
    }
  };

  return (
    <div data-testid="work-customer-create-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,15,15,0.35)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div role="dialog" aria-modal="true" aria-label="고객사 등록"
        style={{ ...card, width: '560px', maxWidth: '100%', maxHeight: '90vh', overflow: 'auto' }}>
        <h2 style={{ margin: '0 0 12px', fontSize: '15px', fontWeight: 600, color: COLORS.text }}>고객사 등록</h2>
        <form onSubmit={submit}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px' }}>
            {CREATE_FIELDS.map((f, i) => (
              <label key={f.key} style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: COLORS.textSub, gridColumn: f.key === 'reason' ? '1 / -1' : undefined }}>
                {f.label}
                {f.kind === 'select' ? (
                  <select value={form[f.key] || ''} onChange={set(f.key)} style={{ ...input, padding: '6px 8px' }}>
                    <option value="">선택 안 함</option>
                    {KIND_OPTIONS.map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                ) : (
                  <input ref={i === 0 ? firstRef : undefined} value={form[f.key] || ''} onChange={set(f.key)}
                    placeholder={f.placeholder} inputMode={f.inputMode} required={f.required} style={input} />
                )}
              </label>
            ))}
          </div>
          {error && <div role="alert" style={{ marginTop: '10px', fontSize: '12px', color: COLORS.dangerText }}>{error}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px', marginTop: '14px' }}>
            <Button variant="secondary" onClick={onClose} disabled={busy}>취소</Button>
            <Button type="submit" disabled={!name || busy}>{busy ? '등록 중…' : '등록'}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function CustomerListCard({
  items, loading, status, onStatus, filters, onFilters, options, canEdit, selectedId, onSelect, onCreated,
}) {
  const [creating, setCreating] = useState(false);
  const opts = options || {};
  const quick = Array.isArray(filters.quick) ? filters.quick : [];
  const toggleQuick = (key) => onFilters({ quick: quick.includes(key) ? quick.filter((k) => k !== key) : [...quick, key] });

  return (
    <section data-testid="work-customer-list" aria-label="고객사별 운영 기준" style={{ ...card, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '6px' }}>
        <h2 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: COLORS.text }}>고객사별 운영 기준</h2>
        <span data-testid="work-customer-count" style={{ fontSize: '12px', color: COLORS.textMuted, marginLeft: 'auto', whiteSpace: 'nowrap' }}>
          {items.length}개사
        </span>
        {canEdit && <Button size="sm" onClick={() => setCreating(true)}>+ 고객사 등록</Button>}
      </div>
      <p style={{ margin: '0 0 10px', fontSize: '12px', color: COLORS.textSub }}>
        업체별 센터와 담당자(영업·운영·센터) 현황입니다. 업체를 누르면 오른쪽에서 포장지시서와 운영 기준을 보고, 견적서를 열 수 있습니다.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', marginBottom: '8px' }}>
        <SearchInput value={filters.q} onChange={(e) => onFilters({ q: e.target.value })} placeholder="업체명·센터담당 검색" width="200px" />
        <select aria-label="센터" value={filters.center} onChange={(e) => onFilters({ center: e.target.value })} style={select}>
          <option value="">전체 센터</option>
          {(opts.center_options || []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select aria-label="운영" value={filters.ops} onChange={(e) => onFilters({ ops: e.target.value })} style={select}>
          <option value="">전체 운영</option>
          {(opts.ops_options || []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select aria-label="영업" value={filters.sales} onChange={(e) => onFilters({ sales: e.target.value })} style={select}>
          <option value="">전체 영업</option>
          {(opts.sales_options || []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select aria-label="상태" value={status} onChange={(e) => onStatus(e.target.value)} style={select}>
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div role="group" aria-label="빠른 필터" style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
        {QUICK_FILTERS.map((f) => {
          const on = quick.includes(f.key);
          return (
            <button key={f.key} type="button" aria-pressed={on} onClick={() => toggleQuick(f.key)} style={toggleBtn(on)}>{f.label}</button>
          );
        })}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table data-testid="work-customer-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>{LIST_COLUMNS.map((h) => <th key={h} scope="col" style={th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={LIST_COLUMNS.length} style={{ ...td, textAlign: 'center', color: COLORS.textMuted }}>불러오는 중…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={LIST_COLUMNS.length} style={{ ...td, textAlign: 'center', color: COLORS.textMuted }}>조건에 맞는 고객사가 없습니다</td></tr>
            )}
            {!loading && items.map((it) => {
              const picked = it.id === selectedId;
              const ended = it.status === STATUS_ENDED;
              const bg = picked ? COLORS.primarySoft : undefined;
              return (
                <tr key={it.id} data-testid="work-customer-row" data-id={it.id} aria-selected={picked}
                  onClick={() => onSelect(it.id)} style={{ cursor: 'pointer', opacity: ended ? 0.55 : 1 }}>
                  <td style={{ ...td, background: bg, whiteSpace: 'nowrap' }}>{dash(it.no)}</td>
                  <td style={{ ...td, background: bg }}>
                    <span style={{ display: 'inline-flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <button type="button" style={nameBtn}>{it.name}</button>
                      {ended && <span style={badge('gray')}>종료</span>}
                    </span>
                  </td>
                  <td style={{ ...td, background: bg }}>{dash(it.centers_raw)}</td>
                  <td style={{ ...td, background: bg }}>{dash(it.sales)}</td>
                  <td style={{ ...td, background: bg }}>{dash(it.ops_main)}</td>
                  <td style={{ ...td, background: bg }}>{dash(it.center_staff)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {creating && (
        <CreateCustomerModal
          onClose={() => setCreating(false)}
          onCreated={(id) => { setCreating(false); onCreated(id); }}
        />
      )}
    </section>
  );
}
